/**
 * Service for fetching and mutating GIF-related resources.
 *
 * Responsibilities:
 * - Fetch gif details and related metadata.
 * - Like/unlike gifs and manage gif settings.
 * - Fetch niches, tags feedback and update it.
 * - Report content, check sound, manage aliases.
 */
import type {
  IGifInfo,
  IPaginatedNicheList,
  IAliasLink,
  ITagFeedbackPayload,
  IGallery,
} from "../types";
import type { ApiClient } from "@/common/api/http/types";

interface ILikeGifParams {
  gifId: string;
  context: string | null;
  source: string;
  sourceId: string | null;
  position: number;
}

interface IUnlikeGifParams {
  gifId: string;
  context: string | null;
}

interface IGifReportParams {
  gifId: string;
  option: number;
  message: string;
  canContact: boolean;
  email: string;
}

interface IPatchGifParams {
  hide_trending?: boolean;
  hide_home?: boolean;
  sexuality?: string;
  published?: boolean;
  description?: string;
  niches?: string[];
  tags?: string[] | null;
}

export default class GifsService {
  public constructor(private readonly client: ApiClient) {}

  async fetchGifById(gifId: string): Promise<IGifInfo> {
    return await this.client.get(`v2/gifs/${gifId}?views=yes&users=yes&niches=yes`);
  }

  async hideGif(props: {
    id: string;
    home: boolean;
    trending: boolean;
  }): Promise<void> {
    await this.client.patch(`v2/gifs/${props.id}`, {
      hide_home: props.home,
      hide_trending: props.trending,
    });
  }

  async patchGif(id: string, params: IPatchGifParams) {
    return await this.client.patch(`v2/gifs/${id}`, params);
  }

  async deleteGif(gifId: string): Promise<void> {
    return await this.client.delete(`v1/me/gifs/${gifId}`);
  }

  async likeGifById({ gifId, context, source, sourceId, position }: ILikeGifParams) {
    return await this.client.put(`v2/gifs/${gifId}/like`, {
      context,
      source,
      source_id: sourceId,
      position,
    });
  }

  async unlikeGifById(params: IUnlikeGifParams) {
    return await this.client.delete(`v2/gifs/${params.gifId}/like`, {
      context: params.context,
    });
  }

  async getGifNiches(gifId: string): Promise<IPaginatedNicheList> {
    return await this.client.get(`v2/gifs/${gifId}/niches`);
  }

  async checkGifSound(gifId: string) {
    return await this.client.post(`v2/gifs/${gifId}/check-sound`);
  }

  async getGifTagsFeedback(gifId: string): Promise<{ tags: string[] }> {
    return await this.client.get(`v2/gifs/${gifId}/tag-feedback`);
  }

  async updateGifTagFeedback(gifId: string, payload: ITagFeedbackPayload) {
    return await this.client.put(`v2/gifs/${gifId}/tag-feedback`, payload);
  }

  async reportGif(params: IGifReportParams) {
    return await this.client.post(`v1/gifs/${params.gifId}/report-content`,
      {
        option: params.option,
        message: params.message,
        canContact: params.canContact,
        email: params.email,
      }
    );
  }

  async addAliasLink(gifId: string): Promise<IAliasLink> {
    return await this.client.post("v2/alias", { gif: gifId });
  }

  async getAliasLinks(gifId: string): Promise<IAliasLink[]> {
    return await this.client.get(`v2/alias/gif/${gifId}`);
  }

  async getAliasLink(aliasId: string) {
    return await this.client.get(`v2/alias/${aliasId}`);
  }

  async removeAliasLink(aliasId: string) {
    return await this.client.delete(`v2/alias/${aliasId}`);
  }

  async getGallery(galleryId: string): Promise<IGallery> {
    return await this.client.get(`v2/gallery/${galleryId}`);
  }
}
